/**
 * `src/lib/ui/theme/boot.ts` — apply the stored reading preferences, once, at
 * start-up, and give the rest of the app the two setters.
 *
 * WHY THERE IS A `bootPreferences` AT ALL RATHER THAN A `$effect` IN A LAYOUT
 * ──────────────────────────────────────────────────────────────────────────
 * The type scale invalidates every measured block height in M2's virtualiser.
 * That invalidation has to happen at a moment the reader can subscribe to, and
 * exactly once per change — not on whatever schedule a component's effect
 * happens to run. So the writes are funnelled through here, and `onChange` is
 * the hook the reader hangs its cache flush on.
 *
 * The first paint is NOT waiting on this. `app.css` defines the light palette on
 * bare `:root` and defaults `--type-scale: 1` / `--leading-adjust: 0`, which are
 * exactly what `SETTING_DEFAULTS['ui.type_scale'] === 1` decodes to. A device
 * that has never opened the settings screen, a device whose IndexedDB read
 * throws, and a device mid-boot all render the same defaults — there is no
 * unstyled flash to guard against and no blocking read to do.
 */

import { applyType, type StyleTarget } from '$lib/ui/type/apply';
import { decodeTypeScale, encodeTypeScale, type TypeScale } from '$lib/ui/type/scale';

import {
	DEFAULT_PREFERENCES,
	loadReadingPreferences,
	saveTheme,
	saveTypeScale,
	type ReadingPreferences
} from './settings';
import { applyTheme, type ResolvedTheme, type ThemeDocument, type ThemeEnvironment } from './theme';

export interface PreferenceHost {
	readonly doc: ThemeDocument & { readonly documentElement: StyleTarget };
	readonly env?: ThemeEnvironment;
	/** Called after every applied change, including the initial one. The M2
	 *  reader flushes its measured-height cache here. */
	readonly onChange?: (state: PreferenceState) => void;
}

export interface PreferenceState extends ReadingPreferences {
	readonly resolvedTheme: ResolvedTheme;
	/** True when the type scale moved, i.e. when a height cache is now stale.
	 *  False on a pure theme change — the optical compensation in the dark
	 *  theme changes stem weight, not metrics, so heights survive it. */
	readonly typeChanged: boolean;
}

export interface Preferences {
	readonly state: () => PreferenceState;
	setTheme: (theme: ReadingPreferences['theme']) => Promise<boolean>;
	setTypeScale: (scale: TypeScale) => Promise<boolean>;
}

function apply(
	host: PreferenceHost,
	next: ReadingPreferences,
	typeChanged: boolean
): PreferenceState {
	applyType(host.doc.documentElement, next.scale);
	const resolvedTheme = applyTheme(host.doc, next.theme, host.env ?? {});
	const state: PreferenceState = { ...next, resolvedTheme, typeChanged };
	host.onChange?.(state);
	return state;
}

/**
 * Read the stored preferences, apply them, and return the two setters.
 *
 * Every setter APPLIES FIRST and persists second. A preference that cannot be
 * written — a full quota, a private window, an evicted origin — is still a
 * preference the user asked for, and refusing to honour it for the session
 * because the disk said no is the wrong trade. The boolean says whether it will
 * survive a reload.
 */
export async function bootPreferences(host: PreferenceHost): Promise<Preferences> {
	let current: ReadingPreferences = DEFAULT_PREFERENCES;
	let state = apply(host, current, true);

	const loaded = await loadReadingPreferences();
	const typeChanged =
		loaded.scale.sizeIndex !== current.scale.sizeIndex ||
		loaded.scale.leadingIndex !== current.scale.leadingIndex;
	if (typeChanged || loaded.theme !== current.theme) {
		current = loaded;
		state = apply(host, current, typeChanged);
	}

	return {
		state: () => state,
		async setTheme(theme) {
			current = { ...current, theme };
			state = apply(host, current, false);
			return saveTheme(theme);
		},
		async setTypeScale(scale) {
			// Round-trip through the codec so what is applied is exactly what a
			// reload will read back — a settings screen must never show a value
			// the packing cannot represent.
			const normalised = decodeTypeScale(encodeTypeScale(scale));
			current = { ...current, scale: normalised };
			state = apply(host, current, true);
			return saveTypeScale(normalised);
		}
	};
}
